import "bootstrap/dist/css/bootstrap.min.css";
import "bootstrap-icons/font/bootstrap-icons.css";
import "../css/Dashboard.css";

import HourlyChatTrafficChart from "../components/DashboardCharts/HourlyChatTrafficChart";
import HourlyActiveUsersChart from "../components/DashboardCharts/HourlyActiveUsersChart";
import UsageOverTimeChart from "../components/DashboardCharts/UsageOverTimeChart";
import TokenUsageChart from "../components/DashboardCharts/TokenUsageChart";
import TotalActiveUsersChart from "../components/DashboardCharts/TotalActiveUsersChart";
import UserPresenceMap from "../components/DashboardCharts/UserPresenceMap";
// import ModelUsageChart from "../components/DashboardCharts/ModelUsageChart";
// import BotPerformance from "../components/DashboardCharts/BotPerformance";
// import Notifications from "../components/DashboardCharts/Notifications";
import { useState, useEffect } from "react";
import api from "../api";
import DisclaimerPopup from "../auth/DisclaimerPopup";
import { showErrorToast, showSuccessToast } from "../utils/toastUtils";

const getIcon = (type) => {
  switch (type) {
    case "totalEnterprise":
      return require("../assets/icons/DashboardsIcons/TotalEnterprise.png");
    case "totaluser":
      return require("../assets/icons/DashboardsIcons/TotalUser.png");
    case "totalmodels":
      return require("../assets/icons/DashboardsIcons/TotalModels.png");
    case "totaltraineddocuments":
      return require("../assets/icons/DashboardsIcons/TotalTrainedDocuments.png");
    case "totaldocumentsize":
      return require("../assets/icons/DashboardsIcons/TotalDocumentsSize.png");
    case "totalchats":
      return require("../assets/icons/DashboardsIcons/TotalChats.png");
    // case "totalnotifications":
    //   return require("../assets/icons/dashboard/notification.png");
    case "totalsystemalert":
      return require("../assets/icons/DashboardsIcons/TotalSystemAlert.png");
    case "totalflaggedmessages":
      return require("../assets/icons/DashboardsIcons/TotalFlaggedMessages.png");
    case "totalabandonment":
      return require("../assets/icons/DashboardsIcons/TotalAbandonment.png");
    case "totalcountries":
      return require("../assets/icons/DashboardsIcons/TotalCountries.png");
    case "averagesessiontime":
      return require("../assets/icons/DashboardsIcons/AverageSessionTime.png");
    case "tbd":
      return require("../assets/icons/DashboardsIcons/TBD.png");
    default:
      return require("../assets/icons/DashboardsIcons/TBD_Icon.png");
  }
};

const Dashboard = () => {
  const [stats, setStats] = useState({
    totalEnterprises: 0,
    activeEnterprises: 0,
    total_used_api_count: 0,
    total_admins: 0,
    total_ips: 0,
    totalCountries: 0,
  });
  const [userName, setUserName] = useState("User");
  const [showDisclaimer, setShowDisclaimer] = useState(false);

  useEffect(() => {
    const user = JSON.parse(
      localStorage.getItem("user") || sessionStorage.getItem("user")
    );
    if (user && user.user_name) {
      setUserName(user.user_name);
    }

    // 👇 Show disclaimer if terms not accepted
    if (user && !user.termsAccepted) {
      setShowDisclaimer(true);
    }
  }, []);

  const token =
    localStorage.getItem("access_token") ||
    sessionStorage.getItem("access_token");

  const fetchStats = async () => {
    if (!token || showDisclaimer) return;

    const config = {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    };

    try {
      const baseUrl = process.env.REACT_APP_API_URL;

      const [enterpriseRes, presenceRes] = await Promise.all([
        api.get(`${baseUrl}/enterprises/counts/all`, config),
        api.get(`${baseUrl}/auth/user-presence`, config),
      ]);

      setStats({
        totalEnterprises: enterpriseRes.data.total,
        activeEnterprises: enterpriseRes.data.active,
        total_used_api_count: enterpriseRes.data.total_used_api_count,
        total_admins: enterpriseRes.data.total_admins,
        total_ips: enterpriseRes.data.total_ips,
        totalCountries: presenceRes.data.total_countries,
      });
    } catch (err) {
      console.error("❌ Error fetching stats", err);
    }
  };

  useEffect(() => {
    fetchStats();
  }, [showDisclaimer]);
  const handleDisclaimerAccept = async () => {
    const user = JSON.parse(
      localStorage.getItem("user") || sessionStorage.getItem("user")
    );

    try {
      await api.post(
        `${process.env.REACT_APP_API_URL}/users/accept-terms`,
        { accepted: true },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const updatedUser = { ...user, termsAccepted: true };
      if (localStorage.getItem("user")) {
        localStorage.setItem("user", JSON.stringify(updatedUser));
      } else {
        sessionStorage.setItem("user", JSON.stringify(updatedUser));
      }

      setShowDisclaimer(false);

      // ✅ Add this line:
      showSuccessToast("Terms accepted successfully.");
    } catch (error) {
      console.error("❌ Failed to accept disclaimer", error);
      showErrorToast("Failed to accept terms. Please try again.");
    }
  };

  const isAdmin  =
    localStorage.getItem("role_name") ||
    sessionStorage.getItem("role_name");

  const cards = isAdmin
    ? [
        {
          label: "Total Admins",
          total: stats.total_admins,
          icon: "totalEnterprise",
        },

        {
          label: "Total Used API Count",
          value: stats.total_used_api_count,
          icon: "totalflaggedmessages",
        },

        {
          label: "Total IPs",
          value: stats.total_ips,
          icon: "totalsystemalert",
        },

        {
          label: "Total Countries",
          value: stats.totalCountries || 0,
          icon: "totalcountries",
        },
      ]
    : [
        {
          label: "Total Enterprise",
          total: stats.totalEnterprises,
          active: stats.activeEnterprises,
          icon: "totalEnterprise",
        },
        {
          label: "Total Used API Count",
          value: stats.total_used_api_count,
          icon: "totalflaggedmessages",
        },

        {
          label: "Total Countries",
          value: stats.totalCountries || 0,
          icon: "totalcountries",
        },
      ];

  return (
    <>
      {showDisclaimer && <DisclaimerPopup onAccept={handleDisclaimerAccept} />}
      <div className="m-2 overflow-y-auto overflow-x-hidden">
        <div className="p-2">
          <h5
            className="mt-2 refresh-prompts mb-4"
            style={{
              fontSize: "18px",
              fontWeight: 600,
              lineHeight: "40px",
            }}
          >
            Hello{" "}
            {userName
              ? userName.charAt(0).toUpperCase() + userName.slice(1)
              : "-"}
            , welcome to the Dashboard!
          </h5>

          {/* STATS CARDS */}
          <div className="stats-container gap-3 mt-0">
            {cards.map((stat, index) => (
              <div key={index} className="dashboard-box p-3 rounded shadow-sm">
                <div className="d-flex align-items-center mb-2">
                  <img
                    src={getIcon(stat.icon)}
                    alt={stat.label}
                    style={{
                      width: "22px",
                      height: "22px",
                    }}
                    className="icon-image1"
                  />
                  <h6 className="ms-2 mb-0 text-nowrap">{stat.label}</h6>
                </div>
                <h2 className="fw-bold">
                  {stat.total !== undefined ? stat.total : stat.value}
                </h2>
                <div className="box-summary">
                  {stat.active !== undefined && stat.active !== null ? (
                    <p className="mb-0">Active: {stat.active}</p>
                  ) : (
                    <p className="mb-0" style={{ visibility: "hidden" }}>
                      Active: 0
                    </p>
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* CHARTS */}
          <div className="row g-4">
            <div className="col-md-6">
              <div className="h-100">
                <HourlyChatTrafficChart />
              </div>
            </div>

            <div className="col-md-6">
              <div className="h-100">
                <HourlyActiveUsersChart />
              </div>
            </div>

            <div className="col-md-6">
              <div className="h-100">
                <UsageOverTimeChart />
              </div>
            </div>

            <div className="col-md-6">
              <div className="h-100">
                <TokenUsageChart />
              </div>
            </div>

            <div className="col-md-6">
              <div className="h-100">
                <TotalActiveUsersChart />
              </div>
            </div>

            <div className="col-md-6">
              <div className="chart-card rounded h-100">
                <UserPresenceMap />
              </div>
            </div>

            {/* <div className="row g-2 p-2">
              <div className="col-md-4">
                <div className="chart-card1 h-100">
                  <ModelUsageChart />
                </div>
              </div>

              <div className="col-md-4">
                <div className="chart-card1 h-100">
                  <BotPerformance />
                </div>
              </div>

              <div className="col-md-4">
                <div className="chart-card1 h-100">
                  <Notifications />
                </div>
              </div>
            </div> */}
          </div>
        </div>
      </div>
    </>
  );
};

export default Dashboard;
